// =============================================================================
// useHopShareStore — persistent "share to client" selection (HOP Slice 3, Stage B+).
//
// Mirrors the acceptance basket: the selection follows the manager across
// categories AND packs within a project, so a progress update can span the
// whole site, not just one category page. Scoped to a single project — switching
// projects clears it. Client-side only (session), no DB draft needed since the
// labels are resolved from the DB at send time.
// =============================================================================

import { create } from "zustand";

interface HopShareStore {
  projectId: string | null;
  selected:  Set<string>;   // asset_ids picked to send
  active:    boolean;       // "Share to client" mode is on
  /** Bind the store to a project; clears everything if the project changed. */
  ensureProject: (projectId: string) => void;
  setActive: (v: boolean) => void;
  toggle:    (assetId: string) => void;
  addMany:   (assetIds: string[]) => void;
  clear:     () => void;
  exit:      () => void;    // leave share mode + clear
}

export const useHopShareStore = create<HopShareStore>((set, get) => ({
  projectId: null,
  selected:  new Set(),
  active:    false,
  ensureProject: (projectId) => {
    if (get().projectId !== projectId) {
      set({ projectId, selected: new Set(), active: false });
    }
  },
  setActive: (v) => set({ active: v }),
  toggle: (assetId) => set((s) => {
    const n = new Set(s.selected);
    n.has(assetId) ? n.delete(assetId) : n.add(assetId);
    return { selected: n };
  }),
  addMany: (assetIds) => set((s) => {
    const n = new Set(s.selected);
    for (const id of assetIds) n.add(id);
    return { selected: n };
  }),
  clear: () => set({ selected: new Set() }),
  exit:  () => set({ selected: new Set(), active: false }),
}));
